import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
    Container,
    Section,
    AmberButton,
} from "@/components/Primitives";

const BLOCKS = [
    {
        n: "01",
        title: "The desk and the trader. The line between them.",
        body: "We draw a hard boundary between analysis and judgment. Deterministic code produces the structural facts about a chart. A reasoning model makes the trade decision from those facts. The code never makes the call. The model never recomputes the indicators. And before the model sees the brief, it is cleaned of anything that would pre-answer the trade — no \u201cstrong setup\u201d labels, no recommended direction, no smuggled verdict. The desk prepares. The trader decides. That boundary is the aegis. It never moves.",
    },
    {
        n: "02",
        title: "The line we hold against ourselves.",
        body: "The most seductive failure mode in this field isn't dishonesty — it's the perfectly reasonable impulse to improve. Watch a session, notice what the system missed, adjust the parameters to catch it next time. It feels like progress. It is curve-fitting. The Aegis Line holds out sessions it never tunes against, freezes the configuration, and tests whether decisions still hold on data it has never seen. A change only counts if it survives that test. And if a change is ever justified with \u201cthis would have caught that trade\u201d — that's the tell, and we stop. That is the line we hold against ourselves.",
    },
    {
        n: "03",
        title: "Beating a dumb baseline.",
        body: "A system that reasons coherently is not the same as a system that makes money. Coherent and profitable feel identical from the inside and only separate when capital is live. So we test the engine against a dumb baseline — entering on every screened candidate, for example. If the disciplined system can't beat the undisciplined one on the same names, it has no edge worth trusting, however well it explains itself. The aegis demands this test. We run it.",
    },
    {
        n: "04",
        title: "Paper is not live. We will tell you when it changes.",
        body: "The Aegis Line is in paper-trading evaluation today. Paper fills happen at quoted prices with no spread, slippage, or partial fills. Paper results are an upper bound, not a forecast. That gap is widest exactly where this strategy lives: thin, low-priced, low-float names with real liquidity constraints. When we move toward live trading, we will start on a very small account, model realistic fills, and tell you plainly what the numbers actually mean. We will not show you a paper curve and let you infer live performance. The line holds here too.",
    },
];

export default function Methodology() {
    return (
        <div className="bg-parchment text-ink">
            <Seo
                title="Methodology"
                description="This is the aegis. Protection isn't a feature of The Aegis Line — it's the whole product. A hard analysis/judgment split, held-out sessions, a dumb-baseline test, and candid paper-vs-live realism."
            />

            {/* Light hero */}
            <section className="border-b border-ink/10">
                <Container className="py-20 sm:py-28">
                    <span className="font-mono text-[0.72rem] uppercase tracking-[0.22em] text-rule-lo">
                        The discipline — the aegis itself
                    </span>
                    <h1 className="mt-5 max-w-4xl text-4xl font-extrabold leading-[1.08] text-ink sm:text-5xl lg:text-6xl">
                        This is the aegis.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
                        Protection isn't a feature of The Aegis Line. It's the
                        whole product. The methodology is what holds the line
                        when everything else is noise. This page is how we hold
                        ourselves to it — and how you can hold us to it.
                    </p>
                </Container>
            </section>

            <Section>
                <Container className="max-w-3xl">
                    {BLOCKS.map((b, i) => (
                        <Reveal key={b.n}>
                            <div className={i === 0 ? "" : "mt-16"}>
                                {i > 0 && (
                                    <div
                                        className="mb-16 h-px w-full"
                                        style={{ background: "var(--rule)" }}
                                        aria-hidden="true"
                                    />
                                )}
                                <span className="font-mono text-sm text-rule-lo">
                                    {b.n}
                                </span>
                                <h2 className="mt-3 text-2xl font-bold leading-tight text-ink sm:text-3xl">
                                    {b.title}
                                </h2>
                                <p className="mt-5 text-base leading-relaxed text-ink/75 sm:text-lg">
                                    {b.body}
                                </p>
                            </div>
                        </Reveal>
                    ))}

                    <Reveal>
                        <div
                            className="mt-16 h-px w-full"
                            style={{ background: "var(--rule)" }}
                            aria-hidden="true"
                        />
                        <div className="mt-16">
                            <p className="max-w-2xl text-lg leading-relaxed text-ink sm:text-xl">
                                If this page made you trust the easy promises a
                                little <span className="italic">less</span> and
                                the hard ones a little{" "}
                                <span className="italic" style={{ color: "var(--rule-lo)" }}>
                                    more
                                </span>{" "}
                                — that was the point. The aegis only works if it
                                holds when it's inconvenient.
                            </p>
                            <div className="mt-8">
                                <AmberButton
                                    to="/legal/risk-disclosure"
                                    testId="methodology-risk-cta"
                                >
                                    Read the full risk disclosure{" "}
                                    <ArrowRight size={16} />
                                </AmberButton>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>
        </div>
    );
}
